// Build the starting GameState for a new Builder run from a content pack + a seed.

import type { BuilderContent } from '../config/types.js';
import type { GameState, Mode } from './types.js';

export type InitialStateArgs = {
  mode: Mode;
  content: BuilderContent;
  rngSeed: number;
  displayName?: string;
  dayKey: string; // wall-clock day this state was created on (UTC yyyy-mm-dd)
};

export function initialState(args: InitialStateArgs): GameState {
  const { mode, content, rngSeed, displayName = 'Kid', dayKey } = args;
  const t = content.tuning;
  return {
    schemaVersion: 1,
    mode,
    rngSeed,
    rngCursor: 0,
    clock: {
      week: 1,
      dayOfWeek: 1,
      lastWallClockDayKey: dayKey,
    },
    ap: {
      current: t.apPerDay,
      perDay: t.apPerDay,
    },
    profile: {
      accountRole: 'child',
      displayName,
      ikigai: { lovesTags: [], goodAtTags: [], worldNeedsTags: [], paidForTags: [] },
      happiness: 50,
      reputation: t.reputation.start,
      creditScore: t.creditScore.start,
      xp: 0,
      level: 1,
      streak: { current: 0, longest: 0 },
    },
    wallet: { cash: t.startingCash },
    ledger: [],
    businesses: [],
    assets: [],
    liabilities: [],
    loans: [],
    savings: { balance: 0, ratePerWeek: t.savingsRatePerWeek },
    services: { active: false, hourlyRate: 15, plannedHours: 0 },
    social: { active: false, followers: 0, postedThisWeek: false },
    gaming: { active: false, skill: 0, weekAction: 'rest' },
    trade: { active: false, inventory: [], boughtSlots: [], boughtSlotsWeek: 0 },
    property: { active: false, portfolio: [], boughtSlots: [], boughtSlotsWeek: 0 },
    broker: { active: false, workingSlot: null, workingWeek: 0, dealsClosed: 0 },
    investors: [],
    holdings: [],
    skills: {
      negotiation: 0,
      marketing: 0,
      quality: 0,
      finance: 0,
      leadership: 0,
    },
    questsActive: [],
    dream: { fundedAmount: 0, targetAmount: 0 },
    weeklyEffects: [],
    lastDecisionWeek: 0,
    pendingConsequences: [],
    landedConsequences: [],
    inSlump: false,
    lastResilienceWeek: 0,
    eventsOut: [],
  };
}
